import {
  AppWindow, Box, Boxes, Container, Database, Fingerprint, Globe, HardDrive, Inbox,
  KeyRound, Layers, LayoutGrid, Library, MapPin, Network, Package, Server,
  ShieldCheck, Split, User, Waypoints, Zap, type LucideIcon,
} from 'lucide-react'

/**
 * Maps a node's semantic `category` (from the backend, see NodePresentation) to
 * a lucide icon + a subtle background tint. The tint strings are literal so
 * Tailwind's scanner keeps them. Health stays on the dot and drift on the
 * outline — tint is the third, independent channel (discovery-model §11).
 */
const ICON: Record<string, LucideIcon> = {
  service: Container,   // a running app container image (docker-ish)
  frontend: AppWindow,
  compute: Server,      // a host / VM
  serverless: Zap,
  database: Database,
  cache: Layers,
  storage: HardDrive,
  network: Network,
  subnet: Waypoints,
  loadbalancer: Split,
  dns: Globe,
  cdn: Globe,
  queue: Inbox,
  security: ShieldCheck,
  secret: KeyRound,
  identity: Fingerprint,
  registry: Package,
  cluster: Boxes,
  region: MapPin,
  library: Library,
  system: LayoutGrid,
  person: User,
  generic: Box,
}

const TINT: Record<string, string> = {
  service: 'bg-blue-50 dark:bg-blue-950/40',
  frontend: 'bg-cyan-50 dark:bg-cyan-950/40',
  compute: 'bg-sky-50 dark:bg-sky-950/40',
  serverless: 'bg-fuchsia-50 dark:bg-fuchsia-950/40',
  database: 'bg-violet-50 dark:bg-violet-950/40',
  cache: 'bg-violet-50 dark:bg-violet-950/40',
  storage: 'bg-amber-50 dark:bg-amber-950/40',
  network: 'bg-teal-50 dark:bg-teal-950/40',
  subnet: 'bg-teal-50 dark:bg-teal-950/40',
  loadbalancer: 'bg-indigo-50 dark:bg-indigo-950/40',
  dns: 'bg-cyan-50 dark:bg-cyan-950/40',
  cdn: 'bg-cyan-50 dark:bg-cyan-950/40',
  queue: 'bg-orange-50 dark:bg-orange-950/40',
  security: 'bg-rose-50 dark:bg-rose-950/40',
  secret: 'bg-rose-50 dark:bg-rose-950/40',
  identity: 'bg-rose-50 dark:bg-rose-950/40',
  registry: 'bg-lime-50 dark:bg-lime-950/40',
  cluster: 'bg-emerald-50 dark:bg-emerald-950/40',
  region: 'bg-emerald-50 dark:bg-emerald-950/40',
  library: 'bg-stone-100 dark:bg-stone-800',
  system: 'bg-neutral-50 dark:bg-neutral-800',
  person: 'bg-neutral-50 dark:bg-neutral-800',
  generic: 'bg-white dark:bg-neutral-800',
}

export const categoryIcon = (category: string): LucideIcon => ICON[category] ?? Box
export const categoryTint = (category: string): string => TINT[category] ?? 'bg-white dark:bg-neutral-800'
